import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Box,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  TextField,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ArrowForward,
  Email,
  Lock,
} from '@mui/icons-material';

// Background image. Empty by default — the dark teal/navy gradient on the
// container already provides the visual. Buyers can set this to any URL
// (their own Firebase Storage / S3 / CDN) to layer a hero image behind it.
const BACKGROUND_IMAGE_URL = process.env.REACT_APP_LOGIN_BACKGROUND_URL || '';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  
  // CRITICAL FIX: Use localStorage flag to persist across remounts
  const INIT_FLAG = 'saheeh_pos_login_initialized';
  
  // Load saved credentials on component mount - ALWAYS on page load
  useEffect(() => {
    console.log('🔐 [LOGIN] Login page loaded - loading saved credentials');
    
    // ALWAYS load saved credentials from localStorage (persists across browser restarts)
    const savedEmail = localStorage.getItem('saheeh_pos_remembered_email');
    const savedPasswordEncoded = localStorage.getItem('saheeh_pos_remembered_password');
    const savedRememberMe = localStorage.getItem('saheeh_pos_remember_me') === 'true';
    
    if (savedEmail && savedRememberMe) {
      setEmail(savedEmail);
      setRememberMe(true);
      console.log('✅ Loaded saved email:', savedEmail);
      
      // Decode and load password if available
      if (savedPasswordEncoded) {
        try {
          // Decode from base64 (basic obfuscation, not real encryption)
          const decodedPassword = atob(savedPasswordEncoded);
          setPassword(decodedPassword);
          console.log('✅ Loaded saved password (decoded)');
        } catch (error) {
          console.error('❌ Error decoding saved password:', error);
          // If decoding fails, clear the corrupted password
          localStorage.removeItem('saheeh_pos_remembered_password');
        }
      }
    } else {
      console.log('ℹ️ No saved credentials found or remember me not enabled');
    }
  }, []); // Run once on mount - will run again after browser restart
  
  // Separate effect for DOM setup (only once per session)
  useEffect(() => {
    // CRITICAL FIX: Check sessionStorage flag to prevent multiple DOM manipulations
    const alreadyInitialized = sessionStorage.getItem(INIT_FLAG) === 'true';
    
    if (alreadyInitialized) {
      console.log('⏸️ [LOGIN] DOM already initialized (session flag), skipping...');
      return;
    }
    
    // Mark as initialized immediately to prevent race conditions
    sessionStorage.setItem(INIT_FLAG, 'true');
    console.log('🎨 [LOGIN] Setting up DOM (ONCE per session)');
    
    // Clear session data (but keep remembered credentials) - ONLY ONCE per session
    localStorage.removeItem('saheeh_pos_session');
    localStorage.removeItem('saheeh_pos_user');
    
    // Set background image - ONLY ONCE
    if (BACKGROUND_IMAGE_URL) {
      const bgImage = `url(${BACKGROUND_IMAGE_URL})`;
      document.body.style.backgroundImage = bgImage;
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundRepeat = 'no-repeat';
      document.documentElement.style.backgroundImage = bgImage;
      document.documentElement.style.backgroundSize = 'cover';
      document.documentElement.style.backgroundPosition = 'center';
      document.documentElement.style.backgroundRepeat = 'no-repeat';
    }
    
    // Inject keyframe animations - ONLY ONCE (check if already exists)
    const styleId = 'login-animations';
    let styleEl = document.getElementById(styleId) as HTMLStyleElement;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    
    // Only set content if not already set (prevent overwriting)
    if (styleEl && !styleEl.textContent) {
      styleEl.textContent = `
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .login-card-container * {
          border-radius: 10px !important;
        }
        .MuiCard-root {
          border-radius: 10px !important;
          overflow: hidden !important;
        }
        .MuiOutlinedInput-root,
        .MuiOutlinedInput-root fieldset,
        .MuiButton-root {
          border-radius: 10px !important;
        }
        .MuiTextField-root .MuiOutlinedInput-root {
          border-radius: 10px !important;
        }
      `;
    }
    
    // Cleanup on unmount (but don't clear sessionStorage flag - let it persist for the session)
    return () => {
      // Only cleanup DOM styles if component is actually unmounting
      // Don't remove the sessionStorage flag - it prevents re-initialization
      console.log('🧹 [LOGIN] Component unmounting, cleaning up DOM styles only');
    };
  }, []); // Empty dependency array - only run once on mount

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError(t('login.error_empty_fields'));
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const FirebaseAuthService = (await import('../../services/firebaseAuthService')).default;
      const authService = FirebaseAuthService.getInstance();
      
      const result = await authService.signIn(email, password);
      
      if (result.success && result.user) {
        // Handle "Remember me" functionality
        if (rememberMe) {
          // Save email to localStorage
          localStorage.setItem('saheeh_pos_remembered_email', email);
          localStorage.setItem('saheeh_pos_remember_me', 'true');
          
          // Save password (encoded with base64 for basic obfuscation)
          // Note: This is NOT real encryption, just basic obfuscation
          // For a production system, consider using proper encryption
          try {
            const encodedPassword = btoa(password); // Base64 encode
            localStorage.setItem('saheeh_pos_remembered_password', encodedPassword);
            console.log('✅ Saved email and password for remember me:', email);
          } catch (error) {
            console.error('❌ Error encoding password:', error);
            // Still save email even if password encoding fails
            console.log('✅ Saved email only (password encoding failed)');
          }
        } else {
          // Clear saved credentials if "Remember me" is unchecked
          localStorage.removeItem('saheeh_pos_remembered_email');
          localStorage.removeItem('saheeh_pos_remembered_password');
          localStorage.removeItem('saheeh_pos_remember_me');
          console.log('✅ Cleared saved credentials');
        }
        
        // Clear initialization flag so it can be set again on next visit
        sessionStorage.removeItem(INIT_FLAG);
        
        navigate('/dashboard');
      } else {
        setError(result.error || t('login.error_failed'));
      }
    } catch (error) {
      setError(t('login.error_network'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        backgroundImage: `url(${BACKGROUND_IMAGE_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflow: 'auto',
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'center',
        padding: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, sm: 2.5, md: 3 },
      }}
    >

        {/* Main Content */}
        <Box
          className="login-card-container"
          sx={{
            position: 'relative',
            zIndex: 10,
            width: '100%',
            maxWidth: '480px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
        {/* Login Card - Dark Glass Background */}
        <Card
          style={{
            backgroundColor: 'rgba(10, 37, 64, 0.85)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          }}
          sx={{
            backgroundColor: 'rgba(10, 37, 64, 0.85)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            borderRadius: '10px',
            padding: { xs: 2, sm: 2.5, md: 3 },
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(0, 200, 150, 0.15)',
            animation: 'fadeInUp 0.8s ease-out 0.2s backwards',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            '& .MuiCard-root': {
              borderRadius: '10px',
            },
          }}
        >
          <CardContent sx={{ p: 0, width: '100%', overflow: 'hidden', borderRadius: '10px' }}>
            {/* Top Header - POINT OF SALE SYSTEM */}
            <Typography
              sx={{
                textAlign: 'center',
                color: '#00C896',
                fontSize: '11px',
                letterSpacing: '2px',
                fontWeight: 400,
                mb: 2,
                textTransform: 'uppercase',
              }}
            >
              {t('login.pos_system')}
            </Typography>

            {/* Restaurant logo */}
            <Box
              sx={{
                textAlign: 'center',
                mb: 2.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box
                component="img"
                src="/saheeh_logo_w.svg"
                alt="Saheeh"
                sx={{
                  width: { xs: 130, sm: 150, md: 170 },
                  height: 'auto',
                  filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.5))',
                }}
              />
            </Box>

            {/* Card Header */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  color: '#ffffff',
                  fontSize: { xs: '22px', sm: '26px', md: '30px' },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                {t('login.welcome_back')}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '13px',
                }}
              >
                {t('login.sign_in_subtitle')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Email Field */}
              <Box>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '13px',
                    fontWeight: 500,
                    mb: 0.75,
                  }}
                >
                  {t('login.email')}
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login.email_placeholder')}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: '#00C896', fontSize: '20px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                      color: '#ffffff',
                      minHeight: '44px',
                      overflow: 'hidden',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 200, 150, 0.4)',
                        borderRadius: '10px',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00C896',
                        borderWidth: '2px',
                        borderRadius: '10px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: '#ffffff',
                      padding: '10px 14px',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </Box>

              {/* Password Field */}
              <Box>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '13px',
                    fontWeight: 500,
                    mb: 0.75,
                  }}
                >
                  {t('login.password')}
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.password_placeholder')}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#00C896', fontSize: '20px' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{
                            color: '#00C896',
                            padding: '2px',
                            width: '24px',
                            height: '24px',
                            '& .MuiSvgIcon-root': {
                              fontSize: '14px',
                            },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                      color: '#ffffff',
                      minHeight: '44px',
                      overflow: 'hidden',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 200, 150, 0.4)',
                        borderRadius: '10px',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00C896',
                        borderWidth: '2px',
                        borderRadius: '10px',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: '#ffffff',
                      padding: '10px 45px 10px 14px',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </Box>

              {/* Remember Me */}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&.Mui-checked': {
                          color: '#00C896',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
                      {t('login.remember_me')}
                    </Typography>
                  }
                />
              </Box>

              {/* Sign In Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                endIcon={loading ? null : <ArrowForward />}
                sx={{
                  py: 1.25,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
                  color: '#0A2540',
                  fontWeight: 700,
                  fontSize: '15px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  overflow: 'hidden',
                  opacity: loading ? 0.85 : 1,
                  cursor: loading ? 'wait' : 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover:not(:disabled)': {
                    background: 'linear-gradient(135deg, #1AD4A6 0%, #00B387 100%)',
                    transform: 'scale(1.02)',
                    boxShadow: '0 8px 24px rgba(0, 200, 150, 0.35)',
                    borderRadius: '10px',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #00C896 0%, #00A37A 100%)',
                    color: '#0A2540',
                    opacity: 0.85,
                    borderRadius: '10px',
                  },
                }}
              >
                {loading ? t('login.signing_in') : t('login.sign_in')}
              </Button>
            </Box>

            {/* Version Info */}
            <Box
              sx={{
                mt: 1.5,
                textAlign: 'center',
                color: '#00C896',
                fontSize: '10px',
                letterSpacing: '1px',
              }}
            >
              v1.0.0 | ZATCA Compliant | Made for Saudi Restaurants | Powered by{' '}
              <Typography
                component="span"
                sx={{
                  color: '#00C896',
                  fontWeight: 600,
                }}
              >
                BlackLayers
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LoginPage;
